"use client";

import { useRef } from "react";

import { Icon } from "@zapier/zinnia-icons";

import styles from "./FolderTreeBranch.module.css";
import newFolderStyles from "./NewFolderRow.module.css";

interface NewFolderRowProps {
  folderLevel: number;
  onCommit: (name: string) => void;
  onCancel: () => void;
  placeholder: string;
  /** Restores the typed name after a failed create attempt. */
  draftName?: string;
  inputAriaLabel: string;
  /** When true, keep the input mounted (CAA) but ignore commit/blur. */
  isCreating?: boolean;
}

/**
 * Inline create row. Matches CAA: the input stays mounted until create
 * finishes so the list does not swap through a spinner row and jump scroll.
 */
export function NewFolderRow({
  folderLevel,
  onCommit,
  onCancel,
  placeholder,
  draftName,
  inputAriaLabel,
  isCreating = false,
}: NewFolderRowProps) {
  const cancelledReference = useRef(false);

  return (
    <li role="none">
      <div
        className={styles.folderTreeNode}
        style={{
          paddingLeft: folderLevel * 24 + 8,
          opacity: isCreating ? 0.6 : 1,
        }}
        role="treeitem"
        aria-selected={false}
        aria-label={inputAriaLabel}
        aria-busy={isCreating || undefined}
      >
        <span className={styles.chevronSpacer} aria-hidden />
        <span className={styles.folderMain}>
          <Icon
            aria-hidden={true}
            name="navFolder"
            color="GrayWarm6"
            className={styles.icon}
            size={18}
          />
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the new-folder name input when it appears
            autoFocus
            className={newFolderStyles.newFolderInput}
            placeholder={placeholder}
            defaultValue={draftName ?? ""}
            aria-label={inputAriaLabel}
            disabled={isCreating}
            onBlur={(event) => {
              if (cancelledReference.current || isCreating) return;
              const value = event.target.value.trim();
              if (value) {
                onCommit(value);
                return;
              }
              onCancel();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                if (isCreating) return;
                const value = (event.target as HTMLInputElement).value.trim();
                if (value) {
                  onCommit(value);
                  return;
                }
                onCancel();
                return;
              }
              if (event.key === "Escape") {
                event.stopPropagation();
                if (isCreating) return;
                cancelledReference.current = true;
                onCancel();
              }
            }}
          />
        </span>
      </div>
    </li>
  );
}
